import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "api/axiosInstance";
import { Libraries, useJsApiLoader } from "@react-google-maps/api";
import { ProSidebarProvider } from "react-pro-sidebar";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { IconContext } from "react-icons";
import { light, dark, getThemeConfig } from "./styles/theme";
import { ThemeProvider } from "styled-components";
import { GlobalStyle } from "./styles/global";

import { Notice } from "components/element/Notice";
import { getCookie } from "cookies/cookie";
import { ConfigProvider } from "antd";
import { ValidAuth } from "redux/reducers/auth/isAuth";
import { useQueryClient } from "react-query";
import Spinner from "components/atoms/loader/Spinner";
import NoData from "components/templates/nopage/NoData";
import { useAuth } from "components/Auth";
import {
  connectStomp,
  disconnectStomp,
  setCurrentAccount,
  setCurrentUser,
} from "redux/stomp/stompSlice";
import SessionExpired from "components/element/SessionExpired";
import DeactivateOrganizationModal from "components/templates/modal/auth/DeactivateOrganizationModal";
import { NoticeProvider } from "components/NoticePovider";
import { useQuery } from "react-query";
import { getReleaseLatestVersion } from "api/releaseAPI";
import { updateReleaseVersion } from "redux/reducers/notices/noticeReducer";
import ChangePermissionModal from "components/templates/modal/auth/ChangePermissionModal";
import RouteChangeTracker from "RouteChangeTracker";
import ErrorPage from "components/templates/nopage/ErrorPage";
import { ErrorBoundary } from "components/blocks/error/ErrorBoundary";
import { ApiError } from "components/blocks/error/apiError";
import { CookieBar } from "components/element/CookieBar";
import ChangeOrganizationModal from "components/templates/modal/auth/ChangeOrganizationModal";
import RoutesContainer from "RoutesContainer";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import { useSelector } from "react-redux";
import { selectStomp } from "redux/stomp/stompSelector";
import { useMenu } from "hook/useMenu";
import { logoutAbleUrl, normalAbleUrl } from "utils/MenuUtil";
import { clearAxiosHeaders } from "utils/AuthUtil";
import { LastPath } from "api/interfaces/authInterface.interface";
import { getRoleId, mergeLastPathForAccountSwitch } from "utils/lastPathUtil";

function App(): JSX.Element {
  const [initLoad, setInitLoad] = useState(false);
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const location = useLocation();
  const { suspendUpdated, needUpdateCurrentAccount, userPermissionUpdated } =
    useSelector(selectStomp);

  const queryClient = useQueryClient();

  const [libraries] = useState<Libraries>(["places", "marker"]);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isAuth: ValidAuth = useAppSelector((state) => state.isAuth);
  const { returnToStartPage, updateCurrentAccount } = useMenu({});
  const isEnableConnect: boolean = useMemo(() => {
    if (!getCookie(REFRESH_TOKEN) && auth.user.userId) return false;
    if (selectedAccount.isSuspended === SUSPENSION_STATUS.DISABLED)
      return false;
    return (
      isAuth.isAuthenticated &&
      !(
        suspendUpdated.status === SUSPENSION_STATUS.DISABLED &&
        suspendUpdated.accountId === selectedAccount.accountId
      )
    );
  }, [
    auth.user.userId,
    selectedAccount.isSuspended,
    selectedAccount.accountId,
    isAuth.isAuthenticated,
    suspendUpdated.status,
    suspendUpdated.accountId,
  ]);

  const themeMode = useAppSelector((state) => state.theme.theme);
  const theme = themeMode === "light" ? light : dark;

  const selectedAccountRef = useRef(selectedAccount);
  selectedAccountRef.current = selectedAccount;

  useEffect(() => {
    if (!auth.user.userId) {
      return;
    }

    const handle = window.setTimeout(() => {
      const account = selectedAccountRef.current;
      if (!account.accountId) {
        return;
      }

      const sp = new URLSearchParams(window.location.search);
      const urlAccountId = sp.get("account");
      if (
        urlAccountId !== null &&
        urlAccountId !== "" &&
        urlAccountId !== account.accountId
      ) {
        return;
      }

      let userType = sp.get("r") as string;
      if (!userType) {
        return;
      }

      let previousPath: LastPath[] = [];
      try {
        previousPath = JSON.parse(
          sessionStorage.getItem("lastPath") ?? "[]"
        ) as LastPath[];
      } catch {
        previousPath = [];
      }

      const nextPath = mergeLastPathForAccountSwitch(
        previousPath,
        account,
        userType
      );
      sessionStorage.setItem(
        "userType",
        JSON.stringify(nextPath.at(0) as LastPath)
      );
      sessionStorage.setItem("lastPath", JSON.stringify(nextPath));
      localStorage.setItem("ut", JSON.stringify(nextPath.at(0) as LastPath));
    }, 0);

    return () => {
      window.clearTimeout(handle);
    };
  }, [location.pathname, location.search, auth.user.userId, selectedAccount]);
  const timer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!auth || !auth.user) {
      return;
    }
    const tryConnect = () => {
      if (auth.user.userId) {
        console.log("connect");
        dispatch(connectStomp(auth.user));
        dispatch(setCurrentUser(auth.user));
      }
    };
    const handleVisibilityChange = (e: Event) => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      if (
        e.target instanceof Document &&
        e.target.visibilityState === "hidden"
      ) {
        console.log("hidden");
        timer.current = setTimeout(() => {
          dispatch(disconnectStomp({ expectReconnect: true }));
        }, 6000);
        return;
      }
      if (!auth.user.userId) {
        return;
      }
      console.log("visible");
      tryConnect();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [auth, auth.user.userId, dispatch]);

  useEffect(() => {
    dispatch(setCurrentAccount(selectedAccount));
  }, [dispatch, selectedAccount]);

  if (process.env.REACT_APP_DATADOG_ENV === "prod") {
    console.log = function no_console() {};
    console.error = function no_console() {};
  }
  useEffect(() => {
    if (needUpdateCurrentAccount) {
      updateCurrentAccount();
    }
  }, [needUpdateCurrentAccount]);

  useEffect(() => {
    try {
      const loadToken = async () => {
        //const token = await sessionStorage.getItem("token");
        if (getCookie(ACCESS_TOKEN)) {
          if (location.pathname === "/" || location.pathname === "/login") {
            if (!auth.user.email) return;
            returnToStartPage();
          }
        } else {
          clearAxiosHeaders();
        }
      };

      loadToken();
    } catch (e) {
      console.log(e);
    } finally {
      setInitLoad(true);
    }
  }, [location.search, auth.user.email]);

  useEffect(() => {
    if (
      !logoutAbleUrl.includes(location.pathname) &&
      location.pathname !== "/login" &&
      location.pathname !== "/" &&
      !normalAbleUrl.includes(location.pathname) &&
      !(
        getCookie(ACCESS_TOKEN) &&
        auth.user.userId &&
        localStorage.getItem("r-mem") &&
        localStorage.getItem("r-hid")
      )
    ) {
      window.location.href = "/login";
    }
  }, [location.pathname]);

  useEffect(() => {
    console.log("queryClient clear");
    queryClient.setDefaultOptions({
      queries: {
        enabled: isEnableConnect,
      },
    });
  }, [
    isAuth,
    queryClient,
    selectedAccount.accountId,
    suspendUpdated.accountId,
    suspendUpdated.status,
  ]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
    language: "en",
    libraries: libraries,
  });

  useQuery(["getReleaseLatestVersion"], () => getReleaseLatestVersion(), {
    refetchOnWindowFocus: false,
    onSuccess: (res: any) => {
      dispatch(
        updateReleaseVersion({
          description: res.description,
          installedVersion: process.env.REACT_APP_VERSION || "",
          latestVersion: res.version,
        })
      );
    },
    onError: (e: any) => {},
  });

  const ignoreHydrationWarning = (error: any) => {
    if (error instanceof Error) {
      // 예: 미미한 hydration 경고성 에러 문자열 패턴 무시
      if (/Hydration failed/i.test(error.message)) return false;
    }
    return !(error instanceof ApiError); // 기본: ApiError 무시
  };

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary
        fallback={(reset) => <ErrorPage />}
        resetKeys={[location.pathname]}
        onError={(err, info) => {}}
        // (선택) 추가 필터 규칙: 특정 메시지는 무시하고 싶다면
        shouldHandle={ignoreHydrationWarning}
      >
        <ProSidebarProvider>
          <ConfigProvider
            renderEmpty={() => <NoData theme={themeMode} />}
            theme={getThemeConfig(themeMode)}
          >
            <IconContext.Provider value={{}}>
              {initLoad && isLoaded ? (
                <NoticeProvider>
                  <Notice />
                  <CookieBar />
                  <RoutesContainer />
                  <SessionExpired />
                  {auth.user.userId && (
                    <>
                      <ChangePermissionModal />
                      <ChangeOrganizationModal />
                    </>
                  )}
                  <RouteChangeTracker />
                  <DeactivateOrganizationModal />
                </NoticeProvider>
              ) : (
                <Spinner />
              )}
            </IconContext.Provider>
          </ConfigProvider>
        </ProSidebarProvider>
      </ErrorBoundary>
      <GlobalStyle />
    </ThemeProvider>
  );
}
export default App;
